import type { MortgageFormState, FormAction, TierFormRow, LumpSumFormRow } from './formTypes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTenorTotal(state: MortgageFormState): number {
  const years = parseInt(state.tenorYears) || 0;
  const months = parseInt(state.tenorAdditionalMonths) || 0;
  return years * 12 + months;
}

function getFixedEnd(state: MortgageFormState): number {
  return state.hasFixedPeriod ? parseInt(state.fixedDurationMonths) || 0 : 0;
}

function newTier(toMonth: string, rate = ''): TierFormRow {
  return { id: crypto.randomUUID(), toMonth, rate };
}

function newLumpSum(amount = '', month = ''): LumpSumFormRow {
  return { id: crypto.randomUUID(), amount, month };
}

// ─── Default state ────────────────────────────────────────────────────────────

/**
 * Returns a fresh default form state.
 * Called as a lazy initializer in useReducer so the date reflects the actual
 * mount time, not the module-import time (avoids stale date after overnight tabs).
 */
export function createDefaultFormState(): MortgageFormState {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return {
    propertyPrice: '500000000',
    downPaymentMode: 'percent',
    downPaymentValue: '20',
    tenorYears: '10',
    tenorAdditionalMonths: '0',
    paymentMethod: 'annuity',
    startDate: `${yyyy}-${mm}-${dd}`,
    calculationMethod: 'fixed_single_floating',
    hasFixedPeriod: true,
    fixedRate: '7.5',
    fixedDurationMonths: '24',
    floatingBaseRate: '11',
    tiers: [],
    includeAdminFee: false,
    adminFeeAmount: '0',
    includeKprFees: false,
    provisionFeePercent: '1',
    appraisalFeeAmount: '0',
    notaryFeePercent: '0.75',
    bphtbPercent: '5',
    ppnEnabled: false,
    ppnPercent: '11',
    insuranceEnabled: false,
    lifeInsurancePremiumPercent: '0',
    fireInsurancePremiumPercent: '0',
    earlyRepaymentMode: 'none',
    extraMonthlyAmount: '',
    extraMonthlyStartMonth: '1',
    extraMonthlyEndMonth: '',
    lumpSums: [],
    financingMode: 'conventional',
    syariahAkadType: 'murabahah',
    syariahMarginPercent: '8',
    syariahUjrahPercent: '8',
    syariahBankSharePercent: '80',
  };
}

// ─── Reducer ──────────────────────────────────────────────────────────────────

export function formReducer(state: MortgageFormState, action: FormAction): MortgageFormState {
  switch (action.type) {
    // ── Loan basics ──────────────────────────────────────────────────────────
    case 'SET_PROPERTY_PRICE':
      return { ...state, propertyPrice: action.value };
    case 'SET_DOWN_PAYMENT_MODE':
      return { ...state, downPaymentMode: action.mode, downPaymentValue: '' };
    case 'SET_DOWN_PAYMENT_VALUE':
      return { ...state, downPaymentValue: action.value };
    case 'SET_TENOR_YEARS':
      return { ...state, tenorYears: action.value };
    case 'SET_TENOR_ADDITIONAL_MONTHS':
      return { ...state, tenorAdditionalMonths: action.value };
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.method };
    case 'SET_START_DATE':
      return { ...state, startDate: action.value };

    // ── Calculation method ───────────────────────────────────────────────────
    case 'SET_CALCULATION_METHOD': {
      const method = action.method;
      if (method === state.calculationMethod) return state;

      if (method === 'fixed_tiered_floating') {
        // Switching to tiered: auto-create first tier if none exist
        if (state.tiers.length === 0) {
          const tenorTotal = getTenorTotal(state);
          const tenorStr = tenorTotal > 0 ? String(tenorTotal) : '';
          return {
            ...state,
            calculationMethod: method,
            tiers: [newTier(tenorStr, state.floatingBaseRate)],
          };
        }
        return { ...state, calculationMethod: method };
      }

      // Switching to fixed_only or fixed_single_floating: clear tiers
      return { ...state, calculationMethod: method, tiers: [] };
    }

    // ── Fixed rate ───────────────────────────────────────────────────────────
    case 'SET_HAS_FIXED_PERIOD':
      return { ...state, hasFixedPeriod: action.value };
    case 'SET_FIXED_RATE':
      return { ...state, fixedRate: action.value };
    case 'SET_FIXED_DURATION_MONTHS':
      return { ...state, fixedDurationMonths: action.value };

    // ── Floating rate ────────────────────────────────────────────────────────
    case 'SET_FLOATING_BASE_RATE':
      return { ...state, floatingBaseRate: action.value };

    // ── Tier management ──────────────────────────────────────────────────────
    case 'ADD_TIER': {
      const tenorTotal = getTenorTotal(state);
      const tiers = state.tiers;

      if (tiers.length === 0) {
        return {
          ...state,
          tiers: [newTier(String(tenorTotal))],
        };
      }

      // Split the last tier at halfway to give user a reasonable starting point
      const lastTier = tiers[tiers.length - 1];
      const fixedEnd = getFixedEnd(state);
      const lastFrom =
        tiers.length === 1
          ? fixedEnd + 1
          : (parseInt(tiers[tiers.length - 2].toMonth) || 0) + 1;
      const lastTo = parseInt(lastTier.toMonth) || tenorTotal;
      const splitPoint = Math.max(lastFrom, Math.floor((lastFrom + lastTo) / 2));

      const updatedLast: TierFormRow = { ...lastTier, toMonth: String(splitPoint) };
      const added: TierFormRow = newTier(String(tenorTotal));

      return {
        ...state,
        tiers: [...tiers.slice(0, -1), updatedLast, added],
      };
    }

    case 'UPDATE_TIER': {
      return {
        ...state,
        tiers: state.tiers.map((t) =>
          t.id === action.id ? { ...t, [action.field]: action.value } : t,
        ),
      };
    }

    case 'REMOVE_TIER': {
      const remaining = state.tiers.filter((t) => t.id !== action.id);
      // Restore last tier's toMonth to tenorTotal after removal
      if (remaining.length > 0) {
        const tenorTotal = getTenorTotal(state);
        const last = remaining[remaining.length - 1];
        remaining[remaining.length - 1] = { ...last, toMonth: String(tenorTotal) };
      }
      return { ...state, tiers: remaining };
    }

    // ── Fees ──────────────────────────────────────────────────────────────────
    case 'SET_INCLUDE_ADMIN_FEE':
      return { ...state, includeAdminFee: action.value };
    case 'SET_ADMIN_FEE_AMOUNT':
      return { ...state, adminFeeAmount: action.value };
    case 'SET_INCLUDE_KPR_FEES':
      return { ...state, includeKprFees: action.value };
    case 'SET_PROVISION_FEE_PERCENT':
      return { ...state, provisionFeePercent: action.value };
    case 'SET_APPRAISAL_FEE_AMOUNT':
      return { ...state, appraisalFeeAmount: action.value };
    case 'SET_NOTARY_FEE_PERCENT':
      return { ...state, notaryFeePercent: action.value };
    case 'SET_BPHTB_PERCENT':
      return { ...state, bphtbPercent: action.value };
    case 'SET_PPN_ENABLED':
      return { ...state, ppnEnabled: action.value };
    case 'SET_PPN_PERCENT':
      return { ...state, ppnPercent: action.value };
    case 'SET_INSURANCE_ENABLED':
      return { ...state, insuranceEnabled: action.value };
    case 'SET_LIFE_INSURANCE_PREMIUM_PERCENT':
      return { ...state, lifeInsurancePremiumPercent: action.value };
    case 'SET_FIRE_INSURANCE_PREMIUM_PERCENT':
      return { ...state, fireInsurancePremiumPercent: action.value };

    // ── Early repayment ───────────────────────────────────────────────────────
    case 'SET_EARLY_REPAYMENT_MODE': {
      const showsLump = action.mode === 'lump_sum' || action.mode === 'both';
      const lumpSums =
        showsLump && state.lumpSums.length === 0 ? [newLumpSum()] : state.lumpSums;
      return { ...state, earlyRepaymentMode: action.mode, lumpSums };
    }
    case 'SET_EXTRA_MONTHLY_AMOUNT':
      return { ...state, extraMonthlyAmount: action.value };
    case 'SET_EXTRA_MONTHLY_START_MONTH':
      return { ...state, extraMonthlyStartMonth: action.value };
    case 'SET_EXTRA_MONTHLY_END_MONTH':
      return { ...state, extraMonthlyEndMonth: action.value };
    case 'ADD_LUMP_SUM':
      return { ...state, lumpSums: [...state.lumpSums, newLumpSum()] };
    case 'UPDATE_LUMP_SUM':
      return {
        ...state,
        lumpSums: state.lumpSums.map((l) =>
          l.id === action.id ? { ...l, [action.field]: action.value } : l,
        ),
      };
    case 'REMOVE_LUMP_SUM':
      return { ...state, lumpSums: state.lumpSums.filter((l) => l.id !== action.id) };

    // ── Syariah mode ─────────────────────────────────────────────────────────
    case 'SET_FINANCING_MODE':
      return { ...state, financingMode: action.mode };
    case 'SET_SYARIAH_AKAD_TYPE':
      return { ...state, syariahAkadType: action.akadType };
    case 'SET_SYARIAH_MARGIN_PERCENT':
      return { ...state, syariahMarginPercent: action.value };
    case 'SET_SYARIAH_UJRAH_PERCENT':
      return { ...state, syariahUjrahPercent: action.value };
    case 'SET_SYARIAH_BANK_SHARE_PERCENT':
      return { ...state, syariahBankSharePercent: action.value };

    // ── Scenario management ───────────────────────────────────────────────────
    case 'RESET_TO_DEFAULT':
      return createDefaultFormState();
    case 'LOAD_STATE':
      return action.state;
  }
}
